import React, { useState, useEffect, useRef } from "react";
import { MapContainer, GeoJSON, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "./TanzaniaMap.css";

// Fix for default marker icons in Leaflet with React
import icon from "leaflet/dist/images/marker-icon.png";
import iconShadow from "leaflet/dist/images/marker-shadow.png";
import { ValidCity } from "../../../components/charts/PrecipitationHistoryChart";
import { tanzaniaRegions, TZRegions } from "../../../data/tz-regions";
import ZoomResetButton from "../../../components/ZoomResetButton";
import * as turf from "@turf/turf";
import { ZoneGeoJsonDataType } from "./RasterValuePopupHandler";
import {
  CLOUDINARY_GEOJSON_BOUNDARY_FOLDER,
  CLOUDINARY_RAW_BASE_URL,
} from "../../../types/cloudinary";
import { Geometry } from "geojson";

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
});
const ZOOM_THRESHHOLD = 5.499;

L.Marker.prototype.options.icon = DefaultIcon;

// Explicitement typer tanzaniaBoundaries s'il n'est pas correctement typé dans le fichier d'origine
// Vous pouvez ajouter cette ligne près de l'import

const boundaryStyle = {
  weight: 3,
  color: "#1a73e8",
  fillColor: "#e8f0fe",
  fillOpacity: 0.2,
  opacity: 1,
  className: "animated-boundary",
  dashArray: "15, 10",
  dashOffset: "0",
};

const zoneStyle = {
  fillOpacity: 0,
  color: "#1a73e8",
  weight: 1.5,
  opacity: 0.6,
};

export type CityOption = {
  id: ValidCity;
  name: string;
  coords?: number[];
  bbox?: number[];
};

interface TanzaniaMapPageProps {
  selectedCity?: string;
  zoneData: any;
  onCitySelect?: (city: CityOption) => void;
}

// const defaultCity: CityOption = {
//   name: "Arusha",
//   id: "arusha",
//   coords: [36.683, -3.3869],
// };

export const getCityOption = (
  id?: string,
  feat?: any,
): CityOption | undefined => {
  feat =
    feat ??
    TZRegions.features.find(
      (pred) => pred?.properties?.id?.toLowerCase() == id?.toLowerCase(),
    );

  if (!feat) return;

  return {
    id: feat.properties?.id,
    name: feat.properties?.name,
    coords: (feat.geometry as any)?.coordinates,
    bbox: feat?.properties?.bbox,
  };
};

export const getZoneBBoxByName = (
  name: string,
  zoneData: ZoneGeoJsonDataType,
): CityOption | undefined => {
  const feature = zoneData?.features.find(
    (f) => f.properties?.ADM1_EN?.toLowerCase() === name?.toLowerCase(),
  );

  if (!feature) return;

  // turf.bbox returns [minX, minY, maxX, maxY]
  const bbox = turf.bbox(feature);

  return {
    id: name as any,
    name: feature?.properties?.ADM1_EN,
    bbox,
  };
};

// Alert: Ensure the order of coordinates passed is [lat, lon]!!
export const getRegionByCoords = (
  coords: [number, number],
): CityOption | undefined => {
  let minDist = Infinity;
  let closestFeat: any = null;

  tanzaniaRegions.features.forEach((feature) => {
    const featureCoords = (feature.geometry as any)?.coordinates;
    if (
      Array.isArray(featureCoords) &&
      typeof featureCoords[0] === "number" &&
      typeof featureCoords[1] === "number"
    ) {
      const dist = Math.sqrt(
        Math.pow(featureCoords[0] - coords[0], 2) +
          Math.pow(featureCoords[1] - coords[1], 2),
      );
      if (dist < minDist) {
        minDist = dist;
        closestFeat = feature;
      }
    }
  });

  if (!closestFeat) return;
  return {
    id: closestFeat.properties?.id,
    name: closestFeat.properties?.name,
    coords: (closestFeat.geometry as any)?.coordinates,
  };
};

const ZoneSelectionHandler = ({
  zoneData,
  selectedRegion,
  onSelect,
}: {
  zoneData: ZoneGeoJsonDataType;
  selectedRegion?: CityOption | null;
  onSelect?: (name: string) => void;
}) => {
  const map = useMap();
  const [hLayer, setHLayer] = useState<L.GeoJSON<any, Geometry> | null>();

  useEffect(() => {
    if (!map) return;

    if (selectedRegion?.name && selectedRegion?.coords) {
      if (
        zoneData &&
        zoneData.type === "FeatureCollection" &&
        zoneData.features
      ) {
        const point = turf.point(selectedRegion?.coords);

        for (const feature of zoneData.features) {
          // Vérifier que la géométrie existe et est du bon type (Polygon ou MultiPolygon)
          if (
            feature.geometry &&
            (feature.geometry.type === "Polygon" ||
              feature.geometry.type === "MultiPolygon")
          ) {
            if (turf.booleanPointInPolygon(point, feature)) {
              if (hLayer) {
                map.removeLayer(hLayer);
              }

              // Add new highlight
              const highlightLayer = L.geoJSON(feature, {
                style: {
                  color: "orange",
                  weight: 3,
                  fillColor: "yellow",
                  fillOpacity: 0.3,
                },
              }).addTo(map);
              setHLayer(highlightLayer);
            }
          }
        }
      }
    }
  }, [selectedRegion, zoneData, map]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const handleClick = (e: L.LeafletMouseEvent) => {
      const clickedLatLng = e.latlng;

      if (
        zoneData &&
        zoneData.type === "FeatureCollection" &&
        zoneData.features
      ) {
        let zoneName;
        const point = turf.point([clickedLatLng.lng, clickedLatLng.lat]);

        for (const feature of zoneData.features) {
          // Vérifier que la géométrie existe et est du bon type (Polygon ou MultiPolygon)
          if (
            feature.geometry &&
            (feature.geometry.type === "Polygon" ||
              feature.geometry.type === "MultiPolygon")
          ) {
            if (turf.booleanPointInPolygon(point, feature)) {
              const name = feature.properties?.ADM1_EN;

              if (name) {
                zoneName = name;
                break;
              }
            }
          }
        }

        if (zoneName) {
          onSelect?.(zoneName);
        }
      }
    };

    map.on("click", handleClick);
  }, [map, zoneData]);

  return null;
};

const TanzaniaMapPage: React.FC<TanzaniaMapPageProps> = ({
  selectedCity,
  zoneData,
  onCitySelect,
}) => {
  // region state to store zone name and coordinates
  const [selectedRegion, setSelectedRegion] = useState<CityOption | null>(null);
  const mapRef = useRef<L.Map | null>(null);

  const [boundaryData, setBoundaryData] = useState<any>(null); // Type GeoJSON

  useEffect(() => {
    const boundaryUrl = `${CLOUDINARY_RAW_BASE_URL}/${CLOUDINARY_GEOJSON_BOUNDARY_FOLDER}/tanzania_boundary.geojson`;

    fetch(boundaryUrl)
      .then((res) => {
        if (!res.ok)
          throw new Error(`Boundary fetch failed: ${res.statusText}`);
        return res.json();
      })
      .then((data) => {
        if (!data) return;

        setBoundaryData(data);
      })
      .catch((err) => {
        throw new Error(`Error fetch failed: ${err}`);
      });
  }, []);

  useEffect(() => {
    const cityObj = getCityOption(selectedCity);

    setSelectedRegion(cityObj ?? null);
  }, [selectedCity]);

  const handleRegionClick = (region?: CityOption) => {
    if (!region) return;
    setSelectedRegion(region);
  };

  useEffect(() => {
    if (!selectedRegion) return;
    onCitySelect?.(selectedRegion);
  }, [selectedRegion]);

  return (
    <div className="flex flex-col h-full">
      <h1 className="text-xl text-center sm:text-left md:text-2xl font-bold mb-4 p-3">
        Tanzania Geographical Map
      </h1>
      <div className="flex-1 relative">
        <MapContainer
          center={[-7.569, 34.888]}
          zoom={ZOOM_THRESHHOLD}
          minZoom={ZOOM_THRESHHOLD}
          style={{ height: "100%", width: "100%", minHeight: "600px" }}
          zoomControl={true}
          ref={(map) => {
            mapRef.current = map;
          }}>
          {/* Utiliser la version typée des frontières de la Tanzanie */}
          {/* <GeoJSON data={typedTanzaniaBoundaries} style={boundaryStyle} /> */}

          {zoneData && (
            <GeoJSON
              key="zones-map1"
              data={zoneData as any} // Caster en 'any' si besoin
              style={zoneStyle}
            />
          )}

          {boundaryData && (
            <GeoJSON
              key="boundaries"
              data={boundaryData as any}
              style={boundaryStyle}
            />
          )}

          {/* Régions de Tanzanie */}
          {/* <GeoJSON
            data={tanzaniaRegions}
            pointToLayer={(_feature, latlng) => {
              const isSelected = _feature.properties?.id === selectedRegion;
              return L.circleMarker(latlng, {
                radius: isSelected ? 12 : 8,
                fillColor: isSelected ? "#4CAF50" : "#ff7800",
                color: isSelected ? "#fff" : "#000",
                weight: isSelected ? 2 : 1,
                opacity: 1,
                fillOpacity: 0.8,
              });
            }}
            onEachFeature={(feature, layer) => {
              const regionId = feature.properties?.id || "";
              const regionName = feature.properties?.name || "";

              layer.on({
                click: () =>
                  handleRegionClick(getCityOption(regionId, feature)),
              });

              layer.bindTooltip(regionName, {
                permanent: feature.properties?.id === selectedRegion,
                direction: "center",
              });
            }}
          /> */}

          <ZoneSelectionHandler
            zoneData={zoneData}
            selectedRegion={selectedRegion}
            onSelect={(name) => handleRegionClick(getCityOption(name))}
          />

          {/* Ajoutez le bouton de reset ici */}
          <ZoomResetButton
            initialCenter={[-7.569, 34.888]}
            initialZoom={ZOOM_THRESHHOLD}
          />
        </MapContainer>
      </div>
    </div>
  );
};

export default TanzaniaMapPage;
